/* eslint-disable */
import * as BABYLON from 'babylonjs';
import CoreUtils from '../utils/coreUtils.js';
import GeoUtils, { UV_SCALE_MULT } from './nodeGraph/geoUtils.js';
import Utils2D from '../utils/utils2D.js';
import Utils3D from './utils/utils3D.js';
import Spline from './nodeGraph/spline.js';

const BEVEL_INTERP_FUNCTIONS = {
    'linear': CoreUtils.interpolate,
    'round': CoreUtils.interpolateCircular,
};

export default class Extrude
{
/*****************************************************************************/
static linearScaleInterp = (fDistanceInterp, fStartScale, fEndScale) =>
{
    let fValue = CoreUtils.interpolate(fDistanceInterp, fStartScale, fEndScale);
    return CoreUtils.clamp(fValue, 1e-5, Number.MAX_VALUE);
};
/*****************************************************************************/
static bevelScaleInterp = (fDistanceInterp, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp) =>
{
    if(!funcStartBevelInterp)
        funcStartBevelInterp = CoreUtils.interpolate;
    if(!funcEndBevelInterp)
        funcEndBevelInterp = CoreUtils.interpolate;

    let fValue;
    if(fDistanceInterp < fStartBevelWidth || fStartBevelWidth >= 1.0)
        fValue = funcStartBevelInterp(1.0 - fDistanceInterp/fStartBevelWidth, fStartScale, fStartBevelScale);
    else if(fDistanceInterp > 1.0 - fEndBevelWidth || fEndBevelWidth >= 1.0)
        fValue = funcEndBevelInterp((fDistanceInterp - 1.0 + fEndBevelWidth)/fEndBevelWidth, fEndScale, fEndBevelScale);
    else
        fValue = CoreUtils.interpolate((fDistanceInterp - fStartBevelWidth)/(1.0 - fStartBevelWidth - fEndBevelWidth), fStartScale, fEndScale);
    return CoreUtils.clamp(fValue, 1e-5, Number.MAX_VALUE);
};
/*****************************************************************************/
static simpleScale = (vecPointIn, fRelScale, shape, iShapeVertex, iPathVertex, axisX, axisY, fCumulOffset, vecScaledPointOut) =>
{
	vecPointIn.scaleToRef(fRelScale, vecScaledPointOut);
    return fRelScale;
};
/*****************************************************************************/
static smartScale = (vecPointIn, fRelScale, shape, iShapeVertex, iPathVertex, axisX, axisY, fCumulOffset, vecScaledPointOut) =>
{
    // Build a spline object from shape points:
    let pSpline = new Spline(shape, true);

    pSpline.offsetCurve(fCumulOffset);

    let vecSize = pSpline.getSize();
    let fOffsetReferenceDim = Math.max(vecSize.x, vecSize.y)/2.0;

    let iNumNewPoints = pSpline.getNumPoints();
    let vecOrigPoint = pSpline.getPoint(iShapeVertex % iNumNewPoints).point;

    let fNewOffset = fOffsetReferenceDim*(fRelScale - 1.0);
    pSpline.offsetCurve(fNewOffset);    

    // Get the new point at the same index:
    iNumNewPoints = pSpline.getNumPoints();
    let vecScaledPoint = pSpline.getPoint(iShapeVertex % iNumNewPoints).point;

    let vecDelta = vecScaledPoint.subtract(vecOrigPoint);

    let vecNewPos = vecPointIn.add(axisX.scale(vecDelta.x)).add(axisY.scale(vecDelta.y));
    vecScaledPointOut.copyFrom(vecNewPos);

    return fNewOffset;
};
/*****************************************************************************/
static createMesh(name, options, scene)
{
    let shape = options.shape;
    let pPathSpline = options.path;

    let capType = options.cap;
	const bDoClose = (options.close === undefined) ? pPathSpline.getIsClosed() : options.close;
	// Don't cap the ends if the path is closed since they won't be
	// visible anyway
	if(bDoClose)
		capType = BABYLON.Mesh.NO_CAP;

    const bIsPath3D = pPathSpline.getIs3D();
    let origPathCenter = pPathSpline.getCenter();
    const strPointsOrient = options.extrusionPlane ? options.extrusionPlane : 'xy';

	let targetStartCenter = null;
	if(options.finalMeshAlign === 'toPath')
	{
		let pointInfo = pPathSpline.getPoint(0);
		if(pointInfo)
		{
			if(bIsPath3D)
				targetStartCenter = pointInfo.point.clone();
			else
				targetStartCenter = new BABYLON.Vector3(pointInfo.point.x, 0, pointInfo.point.y);
		}
	}
	else if(options.finalMeshAlign === 'toShape')
	{
        let shapeBBox = Utils2D.computePointsBBox(shape);
        let vecShapeCenter = shapeBBox.min.add(shapeBBox.max).scale(0.5);
		targetStartCenter = new BABYLON.Vector3(vecShapeCenter.x, 0, vecShapeCenter.y);
	}

    // We need to ensure a consistent orientation for paths, but only if they're closed:
    // Otherwise, we break open paths that are meant to start at the shape, like for door
    // frames.
    if(pPathSpline.getIsClosed())
        pPathSpline.ensureOrientation();

    let path = pPathSpline.getLinearPoints(true, false, bIsPath3D ? '' : strPointsOrient);

	// Generate correct up vector:
	let vecUp;
    if(options.up)
        vecUp = options.up;
	else if(strPointsOrient == 'zy')
		vecUp = new BABYLON.Vector3(1, 0, 0);
	else if(strPointsOrient == 'xy')
		vecUp = new BABYLON.Vector3(0, 0, 1);
	else if(strPointsOrient == 'xz')
		vecUp = new BABYLON.Vector3(0, 1, 0);

    let iNumPathPoints = path.length;
	if(iNumPathPoints < 2 || shape.length <= 2)
		return null;
    let line = BABYLON.Vector3.Zero();
    let nextLine = BABYLON.Vector3.Zero();
    let axisX = BABYLON.Vector3.Zero();
    let axisY = BABYLON.Vector3.Zero();
    let axisZ = BABYLON.Vector3.Zero();
    let nextAxisX = BABYLON.Vector3.Zero();
    let nextAxisY = BABYLON.Vector3.Zero();
    let nextAxisZ = BABYLON.Vector3.Zero();
    let startPoint = BABYLON.Vector3.Zero();
    let nextStartPoint = BABYLON.Vector3.Zero();
    let tempCurrDiff = BABYLON.Vector3.Zero();
    let bisector = BABYLON.Vector3.Zero();
    let distance = 0;
    let ray;
    let allPaths = [];

    let fStartScale = options.startScale !== undefined ? options.startScale : 1.0;
    let fEndScale = options.endScale !== undefined ? options.endScale : 1.0;

    let fStartBevelWidth = options.startBevelWidth !== undefined ? options.startBevelWidth : 0.0;
    let fStartBevelScale = options.startBevelScale !== undefined ? options.startBevelScale : 0.0;
    let fEndBevelWidth = options.endBevelWidth !== undefined ? options.endBevelWidth : 0.0;
    let fEndBevelScale = options.endBevelScale !== undefined ? options.endBevelScale : 0.0;

    const bHavePerStepScale = fStartScale != 1.0 || fEndScale != 1.0 || fStartBevelWidth != 1.0 || fEndBevelWidth != 1.0 || options.funcPerStepShapePointOverride;

    let plane;
    let planeNormal;
    let planeParallel;

    let fSignedArea = GeoUtils.calculateSignedPolygonAreaXY(shape);
    if(fSignedArea > 0)
        shape.reverse();

    // We need to align the shape to the path, otherwise its location affects the final mesh shape.
    let pPathCumulDistances = [];
    let fTotalPathLength = 0;
    let vecShapeShift = new BABYLON.Vector3(0, 0, 0);
    let vecExtraAlignment = new BABYLON.Vector3(0, 0, 0);

    let shapeAlignOffset = new BABYLON.Vector2.Zero();

    let pBBox = Utils2D.computePointsBBox(shape);

    let vecShapeSize = pBBox.max.subtract(pBBox.min);
    let vecShapeCenter = pBBox.max.add(pBBox.min).scale(0.5);

    const yAlignOffset = options.shapeAlignYOffset ? options.shapeAlignYOffset : 0;
    if(options.shapeAlignY == 'inside')
        shapeAlignOffset.y = vecShapeSize.y/2.0 + yAlignOffset;
    else if(options.shapeAlignY == 'outside')
        shapeAlignOffset.y = -vecShapeSize.y/2.0 - yAlignOffset;
    else
        shapeAlignOffset.y -= yAlignOffset;

    const xAlignOffset = options.shapeAlignXOffset ? options.shapeAlignXOffset : 0;

    if(options.shapeAlignX == 'above')
        shapeAlignOffset.x = vecShapeSize.x/2.0 + xAlignOffset;
    else if(options.shapeAlignX == 'below')
        shapeAlignOffset.x = -vecShapeSize.x/2.0 - xAlignOffset;
    else
        shapeAlignOffset.x += xAlignOffset;

    /*
    if(Math.abs(shapeAlignOffset.y) > 1e-3)
	{
        // Only project if we have to
        let testPath;
        if(bIsPath3D)
        {
            testPath = pPathSpline.clone();
            testPath.projectOntoPlane(vecUp, path[0].point);
        }
        else
            testPath = pPathSpline;
        if(!testPath.getIsClockwise())
            shapeAlignOffset.y *= -1;
	}
    */
    
    // Align
    shape.forEach((point) => { 
        point.y += shapeAlignOffset.y;
    });

    const vecInitDir = path[1].subtract(path[0]);
    vecInitDir.normalize();

    let vecLastDir = path[0].subtract(path[path.length - 1]);
    vecLastDir.normalize();
    if(!bDoClose)
        vecLastDir = vecInitDir;
    const fInitDirLastComponent = bIsPath3D ? vecInitDir.z : vecInitDir.y;

    if(strPointsOrient === 'xy')
	{
        vecShapeShift = path[0].subtract(new BABYLON.Vector3(vecShapeCenter.x, 0, vecShapeCenter.y));
        shape.forEach((point) => { point.x += vecShapeShift.x; point.y += vecShapeShift.z; });
        
        if(options.finalMeshAlign === 'toPath')
		{
            targetStartCenter.x -= shapeAlignOffset.y*Math.abs(fInitDirLastComponent);
            targetStartCenter.y -= shapeAlignOffset.y*Math.abs(vecLastDir.x);
		}
	}
    else if(strPointsOrient === 'xz')
	{
        vecShapeShift = path[0].subtract(new BABYLON.Vector3(vecShapeCenter.x, vecShapeCenter.y, 0 ));
        shape.forEach((point) => { point.x += vecShapeShift.x; point.y += vecShapeShift.y - path[0].y; });

        if(options.finalMeshAlign === 'toPath')
		{
            targetStartCenter.x -= shapeAlignOffset.y*Math.abs(fInitDirLastComponent);
            targetStartCenter.z -= shapeAlignOffset.y*Math.abs(vecLastDir.x);
		}        
	}
    else if(strPointsOrient === 'zy')
	{
        vecShapeShift = path[0].subtract(new BABYLON.Vector3(vecShapeCenter.x, 0, vecShapeCenter.y));
        shape.forEach((point) => { point.x += vecShapeShift.z; point.y += vecShapeShift.x; });

        if(options.finalMeshAlign === 'toPath')
		{
            targetStartCenter.y -= shapeAlignOffset.y*Math.abs(fInitDirLastComponent)
            targetStartCenter.z -= shapeAlignOffset.y*Math.abs(vecLastDir.x);
		}
	}

    pPathCumulDistances.push(0);
    for(let iPathVertex = 1; iPathVertex < iNumPathPoints; iPathVertex++)
    {
        path[iPathVertex].subtractToRef(path[iPathVertex - 1], line);
        fTotalPathLength += line.length();
        pPathCumulDistances.push(fTotalPathLength);
    }

    let funcScaleInterp = Extrude.linearScaleInterp;
    if(fStartBevelWidth > 0.0 || fEndBevelWidth > 0.0)
        funcScaleInterp = Extrude.bevelScaleInterp;

    let funcStartBevelInterp = options.startBevelShape ? BEVEL_INTERP_FUNCTIONS[options.startBevelShape] : BEVEL_INTERP_FUNCTIONS['linear'];
    let funcEndBevelInterp = options.endBevelShape ? BEVEL_INTERP_FUNCTIONS[options.endBevelShape] : BEVEL_INTERP_FUNCTIONS['linear'];

    let funcScaleShapePoint = options.scalingMethod ? SCALING_FUNCTIONS[options.scalingMethod] : Extrude.simpleScale;
    let funcScaleShapePointBevelStart = options.startBevelScalingMethod ? SCALING_FUNCTIONS[options.startBevelScalingMethod] : Extrude.simpleScale;
    let funcScaleShapePointBevelEnd = options.endBevelScalingMethod ? SCALING_FUNCTIONS[options.endBevelScalingMethod] : Extrude.simpleScale;

    let funcGetScalingFunction = (fDistanceInterp, fStartBevelAmount, fEndBevelAmount) => {

        if(options.funcPerStepShapePointOverride)
            return options.funcPerStepShapePointOverride;

        if(fDistanceInterp < fStartBevelAmount)
            return funcScaleShapePointBevelStart;
        else if(fDistanceInterp > 1.0 - fEndBevelAmount)
            return funcScaleShapePointBevelEnd;
        else
            return funcScaleShapePoint;
    };

    let funcScalingFunction;

    // Go over every shape vertex and create a path for each one along the length of
    // the extrusion specified by the path curve
    let nextUnscaled = BABYLON.Vector3.Zero();
    let scaledShapePoint = BABYLON.Vector3.Zero();
    let fPrevScale = 1.0;
    for(let iShapeVertex = 0; iShapeVertex < shape.length; iShapeVertex++)
    {
        let cumulScaledOffset = 0.0;

        axisZ = vecInitDir.clone();
        if(vecUp)
            axisX = vecUp;
        else
            axisX = BABYLON.Vector3.Cross(scene.activeCamera.position, axisZ).normalize();
        axisY = BABYLON.Vector3.Cross(axisZ, axisX);

        // Go over every path vertex and compute the path of the current shape vertex
        startPoint = path[0].add(axisX.scale(shape[iShapeVertex].x)).add(axisY.scale(shape[iShapeVertex].y));
        if(bHavePerStepScale)
		{
            startPoint.subtractToRef(path[0], nextUnscaled);
            const fCurrScale = funcScaleInterp(0, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
            funcScalingFunction = funcGetScalingFunction(0, fStartBevelWidth, fEndBevelWidth);
			cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale, shape, iShapeVertex, 0, axisX, axisY, cumulScaledOffset, scaledShapePoint);
            fPrevScale = fCurrScale;
            scaledShapePoint.addToRef(path[0], startPoint);
		}

        let ribbonPath = [];
        if(options.funcOverrideSpecificPoints)
            options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, 0, startPoint);
        ribbonPath.push(startPoint.clone());
        for(let iPathVertex = 0; iPathVertex < iNumPathPoints - 2; iPathVertex++)
        {
            path[iPathVertex + 2].subtractToRef(path[iPathVertex + 1], nextLine);

            nextAxisZ = nextLine.clone().normalize();
            if(vecUp)
                nextAxisX = vecUp;
            else
                nextAxisX = BABYLON.Vector3.Cross(scene.activeCamera.position, nextAxisZ).normalize();
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            nextAxisZ.subtractToRef(axisZ, bisector);

            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);
            plane = BABYLON.Plane.FromPositionAndNormal(path[iPathVertex + 1], planeNormal);

            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            if(distance === null && planeNormal.length() < 1e-5)
            {
                path[iPathVertex + 1].subtractToRef(path[iPathVertex], tempCurrDiff);
                distance = tempCurrDiff.length();
            }
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);

            // We must scale the next start point around the path vertex by the current scaling amount
            if(bHavePerStepScale)
			{
                let fScaleInterp = 0.5;
                if(fTotalPathLength > 0)
                    fScaleInterp = pPathCumulDistances[iPathVertex + 1]/fTotalPathLength;
                const fCurrScale = funcScaleInterp(fScaleInterp, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);

                nextStartPoint.subtractToRef(path[iPathVertex + 1], nextUnscaled);
                funcScalingFunction = funcGetScalingFunction(fScaleInterp, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iPathVertex, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                fPrevScale = fCurrScale;
                scaledShapePoint.addToRef(path[iPathVertex + 1], nextStartPoint);
			}

            // See if we have an override path for this
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iPathVertex, nextStartPoint);

            ribbonPath.push(nextStartPoint.clone());
            axisX = nextAxisX.clone();
            axisY = nextAxisY.clone();
            axisZ = nextAxisZ.clone();
            startPoint = nextStartPoint.clone();
        }

        // Last point
        if(bDoClose)
        {
            path[0].subtractToRef(path[iNumPathPoints - 1], nextLine);
            
            nextAxisZ = nextLine.clone().normalize();
            if(vecUp)
                nextAxisX = vecUp;
            else
                nextAxisX = BABYLON.Vector3.Cross(scene.activeCamera.position, nextAxisZ).normalize();
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            nextAxisZ.subtractToRef(axisZ, bisector);
            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);
            plane = BABYLON.Plane.FromPositionAndNormal(path[iNumPathPoints - 1], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            if(distance === null && planeNormal.length() < 1e-5)
                distance = nextLine.length();
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
            if(bHavePerStepScale)
			{
                nextStartPoint.subtractToRef(path[iNumPathPoints - 1], nextUnscaled);
                const fCurrScale = funcScaleInterp(1, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
				
                funcScalingFunction = funcGetScalingFunction(1, fStartBevelWidth, fEndBevelWidth);
                cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iNumPathPoints - 1, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                fPrevScale = fCurrScale;
                scaledShapePoint.addToRef(path[iNumPathPoints - 1], nextStartPoint);
			}
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints - 1, nextStartPoint);
            ribbonPath.push(nextStartPoint.clone());
            axisX = nextAxisX.clone();
            axisY = nextAxisY.clone();
            axisZ = nextAxisZ.clone();
            startPoint = nextStartPoint.clone();
            path[1].subtractToRef(path[0], nextLine);

            nextAxisZ = nextLine.clone().normalize();
            if(vecUp)
                nextAxisX = vecUp;
            else
                nextAxisX = BABYLON.Vector3.Cross(scene.activeCamera.position, nextAxisZ).normalize();
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            nextAxisZ.subtractToRef(axisZ, bisector);
            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);
            plane = BABYLON.Plane.FromPositionAndNormal(path[0], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            if(distance === null && planeNormal.length() < 1e-5)
                distance = nextLine.length();
            distance = ray.intersectsPlane(plane);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
            if(bHavePerStepScale)
		    {
                nextStartPoint.subtractToRef(path[0], nextUnscaled);
                funcScalingFunction = funcGetScalingFunction(0, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fStartScale/fPrevScale, shape, iShapeVertex, iNumPathPoints, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                scaledShapePoint.addToRef(path[0], nextStartPoint);
		    }
            ribbonPath.shift();
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints, nextStartPoint);
            ribbonPath.unshift(nextStartPoint.clone());
            ribbonPath.push(nextStartPoint.clone());
        } 
        else
        {
            planeNormal = axisZ;
            plane = BABYLON.Plane.FromPositionAndNormal(path[iNumPathPoints - 1], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);

            if(bHavePerStepScale)
			{
                nextStartPoint.subtractToRef(path[iNumPathPoints - 1], nextUnscaled);
                const fCurrScale = funcScaleInterp(1, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
                funcScalingFunction = funcGetScalingFunction(1, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iNumPathPoints - 1, axisX, axisY, cumulScaledOffset, scaledShapePoint);
                scaledShapePoint.addToRef(path[iNumPathPoints - 1], nextStartPoint);
			}

            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints - 1, nextStartPoint);

            ribbonPath.push(nextStartPoint.clone());
        }

        allPaths.push(ribbonPath);
    }

    // Compute bbox and center
    let vecStartMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let vecStartMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    let vecEndMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let vecEndMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
	allPaths.forEach((pointPath) =>
	{
        Utils3D.includePoint(pointPath[0], vecStartMin, vecStartMax);
        Utils3D.includePoint(pointPath[pointPath.length - 1], vecEndMin, vecEndMax);
	});

    let vecStartCenter = vecStartMax.add(vecStartMin).scale(0.5);

    // Compute UVs:   
    const { customUVs, shapeTextureCoords } = Extrude.computeCustomUVsForRibbon(shape, allPaths, bDoClose);

    let vecCenterDiff = BABYLON.Vector3.Zero();
    if(targetStartCenter)
        vecCenterDiff = targetStartCenter.subtract(vecStartCenter.subtract(vecShapeShift)).add(vecExtraAlignment);

    let pMesh = BABYLON.MeshBuilder.CreateRibbon("ribbon", { pathArray: allPaths, sideOrientation: BABYLON.Mesh.FRONTSIDE, closeArray: true, closePath: bDoClose, uvs: customUVs }, scene);
    pMesh.name = name;
    return Extrude.capAndFinalizeMesh(scene, pMesh, allPaths, options, capType, path, bIsPath3D, vecCenterDiff, shapeAlignOffset, bDoClose, origPathCenter, shapeTextureCoords, vecShapeShift, strPointsOrient);
}
/*****************************************************************************/
static computeCustomUVsForRibbon(shape, allPaths, bDoClose)
{
    let nextLine = BABYLON.Vector3.Zero();

    // TODO: Shouldn't we compute these per shape level, since we might be scaling things?
    let cumulDist = 0;
    let currDist;
    let shapeTextureCoords = [0];
    for(let iShapeVertex = 1; iShapeVertex <= shape.length; iShapeVertex++)
	{
        shape[iShapeVertex % shape.length].subtractToRef(shape[iShapeVertex - 1], nextLine);
        currDist = nextLine.length()*UV_SCALE_MULT;
        cumulDist += currDist;
        shapeTextureCoords.push(cumulDist);
	}

    let customUVs = [];
    for(let iShapePointIndex = 0; iShapePointIndex < allPaths.length; iShapePointIndex++)
    {
        let pathCumulDist = 0;
        let iPathStep;
        for(iPathStep = 0; iPathStep < allPaths[iShapePointIndex].length; iPathStep++)    
        {
            if(iPathStep > 0)
            {
                allPaths[iShapePointIndex][iPathStep].subtractToRef(allPaths[iShapePointIndex][iPathStep - 1], nextLine);
                pathCumulDist += nextLine.length()*UV_SCALE_MULT;
            }
            
            let uvPoint = {
                x: shapeTextureCoords[iShapePointIndex], 
                y: pathCumulDist
            };
            customUVs.push(uvPoint);
        }

        // Babylon code adds one more vertex if the close flag is true
        if(bDoClose)
        {
            let iNumPaths = allPaths[iShapePointIndex].length;
            allPaths[iShapePointIndex][0].subtractToRef(allPaths[iShapePointIndex][iNumPaths - 1], nextLine);
            pathCumulDist += nextLine.length()*UV_SCALE_MULT;

            let uvPoint = {
                x: shapeTextureCoords[iShapePointIndex],
                y: pathCumulDist
            };
            customUVs.push(uvPoint);
        }
    }

    return { customUVs, shapeTextureCoords };
}
/*****************************************************************************/
static capAndFinalizeMesh(scene, pMesh, allPaths, options, capType, path, bIsPath3D, vecCenterDiff = null, shapeAlignOffset = null, 
                            bDoClose = true, origPathCenter = null, shapeTextureCoords = null, vecShapeShift = null, strPointsOrient = null)
{
    const origName = pMesh.name;

    const smoothAngle = (options.smoothAngle === undefined) ? 0.0 : options.smoothAngle;

    const uOffset = options.uOffset ? options.uOffset : 0;
    const vOffset = options.vOffset ? options.vOffset : 0;
    const uvRotation = options.uvRotation ? options.uvRotation : 0;

    const capUVRotation = options.capUVRotation ? options.capUVRotation : uvRotation;
    const capUOffset = options.capUOffset ? options.capUOffset : uOffset;
    const capVOffset = options.capVOffset ? options.capVOffset : vOffset;

    const uvOffsetsInWorldSpace = options.uvOffsetsInWorldSpace !== undefined ? options.uvOffsetsInWorldSpace : true;

    // Now, create caps if necessary:
    let otherPolysToMerge = [];
    if(capType == BABYLON.Mesh.CAP_ALL || capType == BABYLON.Mesh.CAP_START)
	{
        let vecSideAxis = allPaths[1][0].subtract(allPaths[0][0]);
        vecSideAxis.normalize();
        let vecInOutAxis = allPaths[2][0].subtract(allPaths[0][0]);
        vecInOutAxis.normalize();
        let vecNormal = BABYLON.Vector3.Cross(vecSideAxis, vecInOutAxis);
        vecNormal.normalize();
        Utils3D.getCoordSystemFromNormal(vecNormal, vecInOutAxis, vecSideAxis);

        let vecCapShape = [];
        for(let iShapeVertex = 0; iShapeVertex < allPaths.length; iShapeVertex++)
		{
            let fX = BABYLON.Vector3.Dot(allPaths[iShapeVertex][0], vecSideAxis);
            let fY = BABYLON.Vector3.Dot(allPaths[iShapeVertex][0], vecInOutAxis);
            vecCapShape.push(new BABYLON.Vector3(fX, 0, fY));
		}

        let polygon = BABYLON.Mesh.CreatePolygon("ribbonCapStart", vecCapShape, scene);
        polygon.rotation.y = Math.PI/2.0;
        polygon.scaling.x *= -1.0;

        let trunkVerts = polygon.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        for(let iShapePointIndex = 0; iShapePointIndex < allPaths.length; iShapePointIndex++)
		{
            trunkVerts[iShapePointIndex*3] = allPaths[iShapePointIndex][0].z;
            trunkVerts[iShapePointIndex*3 + 1] = allPaths[iShapePointIndex][0].y;
            trunkVerts[iShapePointIndex*3 + 2] = allPaths[iShapePointIndex][0].x;
		}

        polygon.setVerticesData(BABYLON.VertexBuffer.PositionKind, trunkVerts, true);

        let fSignedArea = GeoUtils.calculateSignedPolygonAreaXZ(vecCapShape);
        if(fSignedArea <= 0)
            polygon.flipFaces(true);

        GeoUtils.applyBoxUVsToMeshes([polygon], -capUOffset, capVOffset, capUVRotation);
        if(polygon.getTotalIndices() > 0)
        otherPolysToMerge.push(polygon);
        else
            polygon.dispose();
	}

    if(capType == BABYLON.Mesh.CAP_ALL || capType == BABYLON.Mesh.CAP_END)
	{
        let iNumPathPoints = allPaths[0].length;

        let vecSideAxis = allPaths[1][iNumPathPoints - 1].subtract(allPaths[0][iNumPathPoints - 1]);
        vecSideAxis.normalize();
        let vecInOutAxis = allPaths[2][iNumPathPoints - 1].subtract(allPaths[0][iNumPathPoints - 1]);
        vecInOutAxis.normalize();
        let vecNormal = BABYLON.Vector3.Cross(vecSideAxis, vecInOutAxis);
        vecNormal.normalize();
        Utils3D.getCoordSystemFromNormal(vecNormal, vecInOutAxis, vecSideAxis);

        let vecCapShape = [];
        for(let iShapeVertex = 0; iShapeVertex < allPaths.length; iShapeVertex++)
        {
            let fX = BABYLON.Vector3.Dot(allPaths[iShapeVertex][iNumPathPoints - 1], vecSideAxis);
            let fY = BABYLON.Vector3.Dot(allPaths[iShapeVertex][iNumPathPoints - 1], vecInOutAxis);
            vecCapShape.push(new BABYLON.Vector3(fX, 0, fY));
        }

        let polygon = BABYLON.Mesh.CreatePolygon("ribbonCapEnd", vecCapShape, scene);
        polygon.rotation.y = Math.PI/2.0;
        polygon.scaling.x *= -1.0;

        let trunkVerts = polygon.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        for(let iShapePointIndex = 0; iShapePointIndex < allPaths.length; iShapePointIndex++)
		{
            trunkVerts[iShapePointIndex*3] = allPaths[iShapePointIndex][iNumPathPoints - 1].z;
            trunkVerts[iShapePointIndex*3 + 1] = allPaths[iShapePointIndex][iNumPathPoints - 1].y;
            trunkVerts[iShapePointIndex*3 + 2] = allPaths[iShapePointIndex][iNumPathPoints - 1].x;
		}

        polygon.setVerticesData(BABYLON.VertexBuffer.PositionKind, trunkVerts, true);

        let fSignedArea = GeoUtils.calculateSignedPolygonAreaXZ(vecCapShape);
        if(fSignedArea > 0)
            polygon.flipFaces(true);

        GeoUtils.applyBoxUVsToMeshes([polygon], -capUOffset, -capVOffset, -capUVRotation);
        if(polygon.getTotalIndices() > 0)
        otherPolysToMerge.push(polygon);
        else
            polygon.dispose();
	}

    if(options.flatShaded)
	{
        let iStartCapCount = 0;
        let iEndCapCount = 0;

        let vecLocalPointPosition = BABYLON.Vector3.Zero();
        let vecGlobalPointPosition = BABYLON.Vector3.Zero();
        let vecToPathSegmentStart = BABYLON.Vector3.Zero();

        const iClosePathCorr = (bDoClose) ? 1 : 0;
        // If we have caps, we'll apply smoothing later, after merging
        if(otherPolysToMerge.length > 0)
            pMesh.convertToFlatShadedMesh();
        else
            GeoUtils.recomputeNormals(pMesh, smoothAngle);

		pMesh.computeWorldMatrix(true);
		let currWorldMatrix = pMesh.getWorldMatrix();

        let pathCopyWithCapPoints = path.slice();
        if(iStartCapCount > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.unshift(pathCopyWithCapPoints[0].clone());

        if(iEndCapCount > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.push(pathCopyWithCapPoints[pathCopyWithCapPoints.length - 1].clone());

        if(iClosePathCorr > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.push(pathCopyWithCapPoints[0].clone());

        let nextLine = BABYLON.Vector3.Zero();
        let line = BABYLON.Vector3.Zero();
        let cumulDist = 0;
        let cumulDistancesPerPathSegment = [0];
        for(let iPathVertex = 1; iPathVertex < pathCopyWithCapPoints.length; iPathVertex++)
        {
            pathCopyWithCapPoints[iPathVertex].subtractToRef(pathCopyWithCapPoints[iPathVertex - 1], line);
            cumulDist += line.length();
            cumulDistancesPerPathSegment[iPathVertex] = cumulDist;
        }

        // Apply new UVs
        let uvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pMesh.getIndices();
        let fSegmentInterp;
		for(let index = 0; index < indices.length; index += 3)
		{
            // The structure this produces is two triangles per side of the shape (including caps) until all sides are exhausted
            // The iClosePathCorr*2 is because if we close the extrusion path, BJS includes a degenrate polygon at the end.
            // -1 is because we need to know the number of path line segments, not the number of vertices.
            const iNumPathLineSegments = (path.length + iClosePathCorr*2 + iEndCapCount + iStartCapCount) - 1;
            const iFaceIndex = index/3;
            let iPathSegmentIndexInclCaps = Math.floor((iFaceIndex % (iNumPathLineSegments*2))/2) + 1;

            // BJS includes a degenerate polygon at the end in case the close flag is true. Skip it.
            if(iClosePathCorr > 0 && iPathSegmentIndexInclCaps === iNumPathLineSegments)
               continue;

            let iSideIndex = Math.floor(iFaceIndex/(iNumPathLineSegments*2));
            
            pathCopyWithCapPoints[iPathSegmentIndexInclCaps % pathCopyWithCapPoints.length].subtractToRef(pathCopyWithCapPoints[iPathSegmentIndexInclCaps - 1], nextLine);
            let fLength = nextLine.length();
            nextLine.normalize();

			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let pointIndex = indices[index + subIndex];
                vecLocalPointPosition.copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2])				
                BABYLON.Vector3.TransformCoordinatesToRef(vecLocalPointPosition, currWorldMatrix, vecGlobalPointPosition);

                // Project it onto the appropriate path segment
                vecGlobalPointPosition.subtractToRef(pathCopyWithCapPoints[iPathSegmentIndexInclCaps - 1], vecToPathSegmentStart);
                let fCurrDist = BABYLON.Vector3.Dot(nextLine, vecToPathSegmentStart);
                if(fLength > 0.0)
                    fSegmentInterp = fCurrDist/fLength;
                else
                    fSegmentInterp = 0.0;
                let fVCoordinate = (1.0 - fSegmentInterp)*cumulDistancesPerPathSegment[iPathSegmentIndexInclCaps - 1] + fSegmentInterp*cumulDistancesPerPathSegment[iPathSegmentIndexInclCaps];
                fVCoordinate *= UV_SCALE_MULT;
                uvs[pointIndex*2 + 1] = fVCoordinate;

                // This is to take care of the last segment that will span the U coordinate between the last value
                // and 0 at the start. We need to set it to the proper computed distance, otherwise we may have
                // some arbitrary large value tiling back to 0.
                if(iSideIndex === (allPaths.length - 1) && uvs[pointIndex*2 + 0] === 0.0 && shapeTextureCoords)
                    uvs[pointIndex*2 + 0] = shapeTextureCoords[shapeTextureCoords.length - 1];
			}
        }

        pMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
	}

    if(uOffset != 0 || vOffset != 0 || uvRotation != 0)
    {
		let uvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		let indices = pMesh.getIndices();
		let uvPoint = { x: null, y: null };
		let uvRotatedPoint = BABYLON.Vector3.Zero();

		for(let index = 0; index < indices.length; index++)
		{
			const pointIndex = indices[index];
			if(!uvOffsetsInWorldSpace || uvRotation !== 0)
			{
				uvPoint.x = uvs[pointIndex * 2 + 0];
				uvPoint.y = uvs[pointIndex * 2 + 1];
				if(!uvOffsetsInWorldSpace)
				{
					uvPoint.x -= uOffset;
					uvPoint.y -= vOffset;
				}
				Utils2D.rotatePointToRef(uvPoint, uvRotation, uvRotatedPoint);
				uvs[pointIndex * 2 + 0] = uvRotatedPoint.x;
				uvs[pointIndex * 2 + 1] = uvRotatedPoint.y;
			}

			if(uvOffsetsInWorldSpace || uvRotation === 0)
			{
				uvs[pointIndex * 2 + 0] -= uOffset;
				uvs[pointIndex * 2 + 1] -= vOffset;
			}
		}
		pMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
    }

    if(otherPolysToMerge.length > 0)
	{
        otherPolysToMerge.push(pMesh);
        pMesh = BABYLON.Mesh.MergeMeshes(otherPolysToMerge, true, true);
        // The above call can create zero-area, invalid triangles when
        // dealing with collinear vertices, and they will break *everything*
        // down the line, including room booleans. Remove them before they
        // cause any trouble.
        GeoUtils.removeInvalidTriangles(pMesh);
        GeoUtils.recomputeNormals(pMesh, smoothAngle);
	}
    
	let bbox = pMesh.getBoundingInfo().boundingBox;
	let vecCenter = bbox.maximumWorld.add(bbox.minimumWorld).scale(-0.5);
	pMesh.position = vecCenter.clone();
    pMesh.bakeCurrentTransformIntoVertices();
    pMesh.position = vecCenter.scale(-1);
    if(vecCenterDiff && vecShapeShift)
        pMesh.position = pMesh.position.subtract(vecShapeShift).add(vecCenterDiff);

	if(options.finalMeshAlign === 'toPathCenter' && path.length > 0 && origPathCenter)
	{
		let vecCenterPos;
		if(bIsPath3D)
			vecCenterPos = origPathCenter;
		else
			vecCenterPos = new BABYLON.Vector3(origPathCenter.x, 0, origPathCenter.y);
		pMesh.position = vecCenterPos;
	}
    
    if(strPointsOrient && shapeAlignOffset)
	{
        if(strPointsOrient === 'xy')
            pMesh.position.z += shapeAlignOffset.x;
        else if(strPointsOrient === 'xz')
            pMesh.position.y += shapeAlignOffset.x;
        else if(strPointsOrient === 'zy')
            pMesh.position.x += shapeAlignOffset.x;
	}

	if(options.finalMeshAlign === 'toOrigin' || options.finalMeshAlign === 'toOriginBottomAtZero')
	{
		let bbox = pMesh.getBoundingInfo().boundingBox;
		let vecSize = bbox.maximumWorld.subtract(bbox.minimumWorld);
		if(options.finalMeshAlign === 'toOriginBottomAtZero')
			pMesh.position = new BABYLON.Vector3(0, vecSize.y/2.0, 0);
		else
			pMesh.position = new BABYLON.Vector3(0, 0, 0);
	}

    pMesh.name = origName;
    return pMesh;
}
/*****************************************************************************/
}

const SCALING_FUNCTIONS = {
    'scale': Extrude.simpleScale,
    'offset': Extrude.smartScale,
};
